import { useLayoutEffect, useRef, useState } from 'react';

// Retain acknowledged removals just long enough to explain the queue's new order.
export default function useVocabularyPreviewMotion(cards) {
  const [displayed, setDisplayed] = useState(cards);
  const [leaving, setLeaving] = useState([]);
  const [transitioning, setTransitioning] = useState(false);
  const listRef = useRef(null);
  const displayedRef = useRef(displayed);
  displayedRef.current = displayed;
  const layoutRef = useRef(null);
  const animationsRef = useRef([]);
  const finishRef = useRef(null);

  useLayoutEffect(() => {
    const previous = displayedRef.current;
    const unchanged = previous.length === cards.length && previous.every((card, index) => card.cardId === cards[index].cardId);
    animationsRef.current.forEach(animation => animation.cancel());
    clearTimeout(finishRef.current);
    layoutRef.current = null;
    if (unchanged) { setDisplayed(cards); setLeaving([]); setTransitioning(false); return undefined; }
    const nextIds = new Set(cards.map(card => card.cardId));
    const removed = previous.filter(card => !nextIds.has(card.cardId)).map(card => card.cardId);
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    setTransitioning(true);
    setLeaving(removed);
    const timeout = setTimeout(() => {
      const boxes = new Map(Array.from(listRef.current?.children || []).map(element => [element.dataset.cardId, element.getBoundingClientRect()]));
      layoutRef.current = { boxes, reduced };
      setLeaving([]);
      setDisplayed(cards);
    }, removed.length ? (reduced ? 60 : 140) : 0);
    return () => clearTimeout(timeout);
  }, [cards]);

  useLayoutEffect(() => {
    const layout = layoutRef.current;
    if (!layout) return;
    layoutRef.current = null;
    const animations = [];
    for (const element of Array.from(listRef.current?.children || [])) {
      if (!element.animate) continue;
      const previous = layout.boxes.get(element.dataset.cardId);
      const current = element.getBoundingClientRect();
      if (!previous) {
        animations.push(element.animate([{ opacity: 0 }, { opacity: 1 }], { duration: layout.reduced ? 80 : 180, delay: layout.reduced ? 0 : 80, easing: 'ease-out', fill: 'backwards' }));
      } else if (!layout.reduced && (previous.x !== current.x || previous.y !== current.y)) {
        animations.push(element.animate([{ transform: `translate(${previous.x - current.x}px, ${previous.y - current.y}px)` }, { transform: 'translate(0, 0)' }], { duration: 280, easing: 'cubic-bezier(0.16, 1, 0.3, 1)' }));
      }
    }
    animationsRef.current = animations;
    finishRef.current = setTimeout(() => setTransitioning(false), animations.length ? (layout.reduced ? 80 : 280) : 0);
  }, [displayed]);

  useLayoutEffect(() => () => {
    clearTimeout(finishRef.current);
    animationsRef.current.forEach(animation => animation.cancel());
  }, []);

  return { displayed, leaving, transitioning, listRef };
}
